import { useState, useEffect, useCallback, memo } from 'react';
import { TextField, Box, Typography, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { toast } from 'react-toastify';
import { validateField } from '../../../util/validation';

// debounce hook for performance optimization
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const EditableText = memo(function EditableText({
  value,
  setValue,
  isEditable = true,
  type = 'text', // can be 'text', 'date', 'time', 'email', 'phone', 'number'
  displayValue = null,
  label = null,
  placeholder = null,
  isDialog = false,
  schema = null,
  onEditStart = null,
  onEditEnd = null,
  showDefaultAsDisplayValue = false,
  required = false,
  showValidation = false,
  clampLines = 0,
  hyphenate = false,
  truncate = 0,
  truncateMiddle = false,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [validationError, setValidationError] = useState(null);

  const debouncedTempValue = useDebounce(tempValue, 300);

  // update tempValue when value changes
  useEffect(() => {
    setTempValue(value);
  }, [value]);

  useEffect(() => {
    if (showValidation && schema) {
      const validation = validateField(debouncedTempValue, schema);
      setValidationError(validation.isValid ? null : validation.errors[0]);
    }
  }, [debouncedTempValue, schema, showValidation]);

  const handleClick = useCallback(
    e => {
      e.stopPropagation();
      if (isEditable) {
        setIsEditing(true);
        setTempValue(value);
        onEditStart?.();
      }
    },
    [isEditable, value, onEditStart]
  );

  const handleBlur = useCallback(async () => {
    if (schema) {
      const validation = validateField(tempValue, schema);
      if (!validation.isValid) {
        setValidationError(validation.errors[0]);
        return;
      }
    }

    setIsEditing(false);
    onEditEnd?.();

    if (tempValue !== value) {
      const transformedValue = schema?.transformValue
        ? schema.transformValue(tempValue)
        : tempValue;
      const result = await setValue(transformedValue);
      if (result !== true) {
        setTempValue(value);
      } else {
        setValidationError(null);
      }
    }
  }, [tempValue, value, setValue, schema, onEditEnd]);

  const handleKeyPress = useCallback(
    e => {
      if (e.key === 'Enter') {
        handleBlur();
      } else if (e.key === 'Escape') {
        setIsEditing(false);
        setTempValue(value);
        setValidationError(null);
        onEditEnd?.();
      }
    },
    [handleBlur, value, onEditEnd]
  );

  const handleInputChange = useCallback(
    e => {
      const newValue = e.target.value;
      setTempValue(newValue);
      if (isDialog) {
        setValue(newValue);
      }
    },
    [isDialog, setValue]
  );

  // Generic clipboard copy. Activated by `schema.copyable === true`. Copies the
  // raw underlying value (not any truncated/formatted display string), so the
  // operator always gets the full address / number / id back.
  const isCopyable = Boolean(schema?.copyable) && value !== null && value !== undefined && String(value).length > 0;
  const handleCopy = useCallback(
    async e => {
      e.stopPropagation();
      try {
        const raw = String(value ?? '');
        if (!raw) return;
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(raw);
        } else {
          const ta = document.createElement('textarea');
          ta.value = raw;
          ta.setAttribute('readonly', '');
          ta.style.position = 'absolute';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        toast.success(schema?.copyToastMessage || 'In Zwischenablage kopiert.');
      } catch (err) {
        toast.error('Kopieren fehlgeschlagen.');
      }
    },
    [value, schema]
  );

  const renderDisplay = useCallback(() => {
    let displayContent;
    if (type === 'date' || type === 'datetime-local') {
      if (value !== undefined && value !== null) {
        try {
          const dateObj = new Date(value);
          if (!isNaN(dateObj.getTime())) {
            displayContent =
              type === 'datetime-local'
                ? dayjs(dateObj).format('DD.MM.YYYY HH:mm')
                : dayjs(dateObj).format('DD.MM.YYYY');
          } else {
            displayContent = '—';
          }
        } catch (error) {
          displayContent = '—';
        }
      } else if (showDefaultAsDisplayValue) {
        if (placeholder) {
          displayContent = placeholder;
        } else if (label) {
          displayContent = label;
        } else {
          displayContent = '';
        }
      } else {
        displayContent = '—';
      }
    } else if (type === 'time') {
      if (value !== undefined && value !== null) {
        try {
          const normalized = String(value).slice(0, 5);
          const isValid = /^([01]\d|2[0-3]):[0-5]\d$/.test(normalized);
          displayContent = isValid ? normalized : '—';
        } catch (error) {
          displayContent = '—';
        }
      } else if (showDefaultAsDisplayValue) {
        if (placeholder) {
          displayContent = placeholder;
        } else if (label) {
          displayContent = label;
        } else {
          displayContent = '';
        }
      } else {
        displayContent = '—';
      }
    } else if (displayValue && value !== undefined && value !== null) {
      displayContent = displayValue(value);
    } else if (value !== undefined && value !== null) {
      displayContent = value;
    } else if (showDefaultAsDisplayValue) {
      if (placeholder) {
        displayContent = placeholder;
      } else if (label) {
        displayContent = label;
      } else {
        displayContent = '';
      }
    } else {
      displayContent = '—';
    }
    // Optionally truncate long text content to a fixed number of characters
    if (
      Number.isFinite(truncate) &&
      truncate > 0 &&
      (typeof displayContent === 'string' || typeof displayContent === 'number')
    ) {
      const asString = String(displayContent);
      if (asString.length > truncate) {
        if (truncateMiddle && truncate > 1) {
          const left = Math.ceil((truncate - 1) / 2);
          const right = Math.max(0, truncate - 1 - left);
          const start = asString.slice(0, left);
          const end = right > 0 ? asString.slice(-right) : '';
          displayContent = `${start}…${end}`;
        } else {
          displayContent = `${asString.slice(0, truncate)}…`;
        }
      }
    }

    return (
      <Box
        onClick={e => {
          if (isEditable) {
            handleClick(e);
            return;
          }
          if (isCopyable) {
            handleCopy(e);
            return;
          }
          e.stopPropagation();
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: isEditable || isCopyable ? 'pointer' : 'default',
          p: 1,
          borderRadius: 1,
          '&:hover': isEditable
            ? {
                bgcolor: 'action.hover',
                '.edit-icon': { opacity: 1 },
                '.copy-icon': { opacity: 1 },
              }
            : isCopyable
              ? {
                  bgcolor: 'action.hover',
                  '.copy-icon': { opacity: 1 },
                }
              : {},
          position: 'relative',
          width: '100%',
          minHeight: '40px',
        }}
      >
        <Typography
          sx={{
            flex: 1,
            ...(clampLines > 0
              ? {
                  display: '-webkit-box',
                  WebkitLineClamp: clampLines,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-word',
                  hyphens: hyphenate ? 'auto' : 'manual',
                }
              : Number.isFinite(truncate) && truncate > 0
                ? {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordBreak: 'normal',
                    hyphens: 'none',
                  }
                : {}),
          }}
        >
          {displayContent}
        </Typography>
        {isCopyable && (
          <Tooltip title={schema?.copyTooltip || 'In Zwischenablage kopieren'} placement='top'>
            <IconButton
              className='copy-icon'
              size='small'
              onClick={handleCopy}
              sx={{
                ml: 0.5,
                p: 0.25,
                color: 'action.active',
                opacity: 0.4,
                transition: 'opacity 0.2s',
              }}
            >
              <ContentCopyIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
        )}
        {isEditable && (
          <EditIcon
            className='edit-icon'
            sx={{
              fontSize: 'small',
              ml: 1,
              color: 'action.active',
              opacity: 0,
              transition: 'opacity 0.2s',
            }}
          />
        )}
      </Box>
    );
  }, [
    value,
    displayValue,
    isEditable,
    handleClick,
    type,
    placeholder,
    label,
    showDefaultAsDisplayValue,
    clampLines,
    hyphenate,
    truncate,
    truncateMiddle,
    isCopyable,
    handleCopy,
    schema,
  ]);

  const renderEdit = useCallback(() => {
    let inputValue = tempValue !== undefined && tempValue !== null ? tempValue : '';
    if (type === 'date' && tempValue) {
      try {
        const date = new Date(tempValue);
        if (!isNaN(date.getTime())) {
          // Use local date to avoid timezone offset issues
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          inputValue = `${year}-${month}-${day}`;
        }
      } catch (error) {
        inputValue = '';
      }
    }
    if (type === 'datetime-local' && tempValue) {
      try {
        const date = new Date(tempValue);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          inputValue = `${year}-${month}-${day}T${hours}:${minutes}`;
        }
      } catch (error) {
        inputValue = '';
      }
    }
    if (type === 'time' && tempValue) {
      try {
        const str = String(tempValue);
        inputValue = str.length >= 5 ? str.slice(0, 5) : str;
      } catch (error) {
        inputValue = '';
      }
    }

    // Multiline support driven by the field schema. When `schema.multiline` is
    // set, render a multi-row textarea. Pressing Enter then inserts a newline
    // instead of committing, so committing happens on blur (or Escape to cancel).
    const isMultiline = Boolean(schema?.multiline);
    const multilineRows = isMultiline ? schema?.rows || 4 : undefined;

    return (
      <Box sx={{ width: '100%' }}>
        <TextField
          fullWidth
          size='small'
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onKeyDown={isMultiline ? undefined : handleKeyPress}
          autoFocus={isDialog ? false : true}
          type={isMultiline ? 'text' : type}
          multiline={isMultiline}
          minRows={multilineRows}
          label={label}
          placeholder={placeholder}
          required={required}
          error={showValidation && !!validationError}
          helperText={showValidation ? validationError : schema?.description || undefined}
          sx={{
            minWidth: schema?.minWidth || '100px',
            '& .MuiFormHelperText-root': {
              margin: 0,
              fontSize: '0.75rem',
            },
          }}
          InputLabelProps={type === 'date' || type === 'time' || type === 'datetime-local' ? { shrink: true } : undefined}
        />
      </Box>
    );
  }, [
    tempValue,
    handleBlur,
    handleKeyPress,
    type,
    label,
    placeholder,
    isDialog,
    required,
    showValidation,
    validationError,
    schema,
    handleInputChange,
  ]);

  if (isDialog) {
    return renderEdit();
  }

  return isEditing ? renderEdit() : renderDisplay();
});

export default EditableText;
